import { useMemo, useState } from 'react';
import { getResults } from '@shared/api/candidateApi.ts';
import { Alert, Button } from '@shared/components/Button.tsx';
import { Icon } from '@shared/components/Icon.tsx';
import { Loading, Skeleton } from '@shared/components/Skeleton.tsx';
import { type Group, groupOf, GROUPS, OPEN_SUBTYPES } from '@shared/lib/enums.ts';
import { formatDateMr, toDeva } from '@shared/lib/format.ts';
import { useAsync } from '@shared/lib/useAsync.ts';
import type { RoundResult, VenueResult } from '@shared/types.ts';
import { COMPETITION } from '../content/competition.ts';
import { PageBanner } from '../Layout.tsx';
import { RESULTS_SOURCE } from '../results/config.ts';
import { PLACEHOLDER_RESULTS } from '../results/placeholder.ts'; // PLACEHOLDER — delete this line with placeholder.ts

async function loadResults(): Promise<RoundResult[]> {
  if (RESULTS_SOURCE === 'none') return [];
  if (RESULTS_SOURCE === 'placeholder') {
    await new Promise((r) => setTimeout(r, 700)); // show the loading state like a real fetch
    return PLACEHOLDER_RESULTS; // PLACEHOLDER — replace with `return [];` if you delete placeholder.ts
  }
  const rounds = await getResults();
  return [...rounds].sort((a, b) => b.round_number - a.round_number);
}

const RANK_LABEL = ['', 'प्रथम', 'द्वितीय', 'तृतीय'];

export function ResultsPage() {
  const res = useAsync(loadResults, []);
  const [type, setType] = useState<Group | 'all'>('all');
  const [roundId, setRoundId] = useState<string | null>(null);

  const rounds = res.data ?? [];
  const round = rounds.find((r) => r.round_id === roundId) ?? rounds[0];

  return (
    <>
    <PageBanner title='निकाल' />
    <div className='container content'>
      <p className='step-sub'>प्रत्येक फेरी अनेक केंद्रांवर होते. प्रत्येक केंद्राचे विजेते गटनिहाय खाली दिले आहेत.</p>

      {RESULTS_SOURCE === 'placeholder' && res.status !== 'loading' && rounds.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Alert tone='warn' title='नमुना निकाल'>
            हे खरे निकाल नाहीत — हे पान कसे दिसेल हे दाखवण्यासाठी नमुना माहिती आहे.
          </Alert>
        </div>
      )}

      {res.status === 'loading' && !res.data && <ResultsSkeleton />}

      {res.status === 'error' && (
        <Alert tone='error' title='निकाल लोड होऊ शकले नाहीत'
          action={<Button size='sm' variant='secondary' icon='refresh' onClick={res.reload}>पुन्हा प्रयत्न करा</Button>}>
          इंटरनेट जोडणी तपासून पुन्हा प्रयत्न करा.
        </Alert>
      )}

      {res.status === 'success' && rounds.length === 0 && <NoRoundsYet />}

      {round && (
        <>
          <div className='results-controls'>
            <div className='seg' role='tablist' aria-label='फेरी'>
              {rounds.map((r) => (
                <button type='button' key={r.round_id} role='tab' aria-selected={r.round_id === round.round_id}
                  className={`seg-btn ${r.round_id === round.round_id ? 'is-on' : ''}`} onClick={() => setRoundId(r.round_id)}>
                  {r.round_name}
                </button>
              ))}
            </div>
            <div className='filter-chips' role='group' aria-label='गट'>
              {[{ value: 'all', label: 'सर्व गट' }, ...GROUPS].map((t) => (
                <button key={t.value} type='button' aria-pressed={type === t.value}
                  className={`fchip ${type === t.value ? 'is-on' : ''}`} onClick={() => setType(t.value as Group | 'all')}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <p className='round-meta'>
            <Icon name='calendar' size={18} />
            <span>{round.round_name} · {formatDateMr(round.held_on)} · {toDeva(round.venues.length)} केंद्रे</span>
          </p>

          <div className='venues'>
            {round.venues.map((v) => <VenueCard key={v.venue_id} venue={v} type={type} />)}
          </div>
        </>
      )}
    </div>
    </>
  );
}

const subtypeLabel = (t: string) => OPEN_SUBTYPES.find((o) => o.value === t)?.label;

function VenueCard({ venue, type }: { venue: VenueResult; type: Group | 'all' }) {
  // Winners are ranked per group (school / open). Open winners show whether
  // they're a college student or working, under their name.
  const groups = useMemo(() => {
    const gs = type === 'all' ? GROUPS.map((g) => g.value) : [type];
    return gs
      .map((t) => ({ t, winners: venue.winners.filter((w) => groupOf(w.candidate_type) === t).sort((a, b) => a.rank - b.rank) }))
      .filter((g) => g.winners.length);
  }, [venue, type]);

  return (
    <article className='venue'>
      <header className='venue-head'>
        <Icon name='pin' size={20} />
        <div>
          <h3>{venue.city}</h3>
          <p>{venue.venue_name}</p>
        </div>
      </header>
      {groups.length === 0 ? <p className='venue-empty'>या गटात या केंद्रावर विजेते नाहीत.</p> : groups.map((g) => (
        <section key={g.t} className='venue-group'>
          <h4 className={`chip chip-${g.t}`}>{GROUPS.find((x) => x.value === g.t)?.label}</h4>
          <ol className='winners'>
            {g.winners.map((w) => (
              <li key={`${w.rank}-${w.name}`} className={`winner rank-${w.rank}`}>
                <span className='medal' aria-label={RANK_LABEL[w.rank] ?? `${w.rank}`}>{toDeva(w.rank)}</span>
                <div className='winner-text'>
                  <strong>{w.name}</strong>
                  <span>
                    {w.candidate_type !== 'school' && <em className='winner-kind'>{subtypeLabel(w.candidate_type)} · </em>}
                    {w.affiliation}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </article>
  );
}

function NoRoundsYet() {
  return (
    <div className='empty-state'>
      <span className='empty-icon'><Icon name='trophy' size={34} /></span>
      <h2>अद्याप एकही फेरी झालेली नाही</h2>
      <p>
        {COMPETITION.firstRoundDate
          ? <>पहिली फेरी <strong>{formatDateMr(COMPETITION.firstRoundDate)}</strong> रोजी होईल. निकाल येथे जाहीर केले जातील.</>
          : 'पहिल्या फेरीची तारीख लवकरच जाहीर केली जाईल. निकाल येथे जाहीर केले जातील.'}
      </p>
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <Loading label='निकाल लोड होत आहेत…'>
      <div className='results-controls'>
        <Skeleton w={260} h={44} r={10} />
        <Skeleton w={300} h={36} r={999} />
      </div>
      <div className='venues' style={{ marginTop: 24 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className='venue'>
            <Skeleton w='45%' h={24} style={{ marginBottom: 8 }} />
            <Skeleton w='65%' h={14} style={{ marginBottom: 22 }} />
            {[0, 1, 2].map((j) => (
              <div key={j} style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <Skeleton w={34} h={34} r={999} />
                <div style={{ flex: 1 }}>
                  <Skeleton w='55%' h={16} style={{ marginBottom: 6 }} />
                  <Skeleton w='80%' h={12} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Loading>
  );
}
